import {SRPSearchFlow} from 'hooks/useGetSRPSearchFlow'

import {
  PLUS_OFFERS_TOKEN,
  SIGN_UP_TOKEN
} from '@vio/core/src/offer/business/privateDeals'

/**
 * The hotel and dates a request is for
 */
export type Itinerary = Pick<
  SearchUrlParams,
  'hotelId' | 'checkIn' | 'checkOut'
>

export enum LayoutType {
  /** Split view - renders both list and map */
  SPLIT = 'split',
  /** List view - renders list view only */
  LIST = 'list',
  /** Map view - renders map only */
  MAP = 'map'
}

export enum LocationSearchType {
  /**
   * Gets the geolocation data from the location of the ip address
   */
  IpLocation = 'ipLocation',
  /**
   * Gets the geolocation data from the user's device
   */
  UserLocation = 'userLocation',
  /**
   * Gets the geolocation data from the url
   */
  UrlLocation = 'urlLocation'
}

export enum MapDisplayAction {
  /** Map should not be visible on the page */
  HIDE = 'hide',
  /** Map should be visible on the page */
  SHOW = 'show',
  /** Map should be expanded */
  EXPAND = 'expand',
  /** Map should be minimize */
  MINIMIZE = 'minimize'
}

export type SortField =
  | 'price'
  | 'popularity'
  | 'privateDeals'
  | 'distance'
  | 'travelHack'
  | 'guestRating'
  | 'starRating'
  | 'seo'
export type SortOrder = 'ascending' | 'descending'

export type FilterUrlParam = number | string | string[] | BooleanUrlParam

export interface FilterUrlParams {
  chainIds?: string[]
  facilities?: string[]
  freeCancellation?: BooleanUrlParam
  reviewScore?: string
  hotelName?: string
  noHostels?: BooleanUrlParam
  priceMax?: number
  priceMin?: number
  propertyTypes?: string[]
  starRatings?: string[]
  themes?: string[]
  sortField?: SortField
  sortOrder?: SortOrder
  amenities?: string[]
  offerFilters?: string
  roomFilters?: string
  payLater?: BooleanUrlParam
  exclusive?: BooleanUrlParam
  brandIds?: string[] | string
  neighborhoodIds?: string[] | string
  dealFreeze?: BooleanUrlParam // Added as a part of c747927f-deal-freeze-srp
}

export interface SortUrlParams {
  sortField: SortField
  sortOrder: SortOrder
}

// TODO: dedupe with SearchParamsType in types/search (root types)
export interface SearchUrlParams
  extends FilterUrlParams,
    Partial<SortUrlParams> {
  anonymousId?: string
  boundingBox?: Array<string | number>
  checkIn?: string
  checkInDistance?: number
  checkOut?: string
  clickId?: string
  openSearchBox?: string
  currency?: string
  epv?: string
  esd?: string
  forceCurrencyChange?: BooleanUrlParam
  forceLanguageChange?: BooleanUrlParam
  fsmr?: string
  homeSearch?: BooleanUrlParam
  hotelId?: string
  hotelSlug?: string
  bookingId?: string
  label?: string
  lang?: string
  layout?: LayoutType
  mlos?: number
  nights?: number
  optimizeAnchorRooms?: BooleanUrlParam
  optimizeRooms?: BooleanUrlParam
  iataCode?: string
  placeId?: string
  profile?: string
  inAppFirst?: BooleanUrlParam // Added as part of sc-185842 — Kiwi in-app browser entry signal
  redirectId?: string
  rooms?: string
  sbc?: string
  scrollAnchorId?: string
  token?: typeof SIGN_UP_TOKEN | typeof PLUS_OFFERS_TOKEN
  userInteractedWithFilters?: BooleanUrlParam
  userLocationSearch?: BooleanUrlParam
  userSearch?: BooleanUrlParam
  utm_source?: string
  includeTaxes?: '0' | '1'
  showTotal?: '0' | '1'
  clickedOffer?: string
  clickedBundle?: string
  address?: string
  clickedOfferRate?: number
  deviceId?: string
  lat?: string
  lon?: string
  // Whether anchor hotel was selected in SRP - only viable on AP
  isAnchor?: string
  isAlertsManagementSearch?: string
  preferredRate?: number
  roomUserSearch?: string
  overlay?: string
  /**
   * show date confirmation tooltip
   * it was added as part of this task: https://app.shortcut.com/findhotel/story/154347/update-landing-experience-for-users-with-no-dates-default-dates-widget-traffic
   */
  bookingHint?: '0' | '1'
  destinationPageSearch?: BooleanUrlParam
  // User landing on an SEO flow
  srpSearchFlow?: SRPSearchFlow
  // AP deep-link scroll anchors, consumed once on landing then stripped
  scrollToRooms?: BooleanUrlParam
  scrollToRoom?: string
  // SRP "More deals" hand-off, consumed once on landing then stripped
  showAllOffers?: BooleanUrlParam
  /** Map zoom of an area search, so a refresh shows the same view */
  zoomLevel?: number | string
}

export type FilterGroups = Partial<Record<keyof FilterUrlParams, FilterGroup>>

export interface FilterGroup {
  [key: string]: FilterOptionItem
}
export interface FilterOptionItem {
  id: string
  value: number
  label: string
  isAvailable: boolean
}
